import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  AppUpdate,
  AppUpdateAvailability,
  AppUpdateResult,
  AppUpdateResultCode,
} from '@capawesome/capacitor-app-update';
import { HelperService } from '../helper/helper.service';
import { TxService } from '../shared/tx.service';
import { firstValueFrom, Observable } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { UpdateAppModalComponent } from 'src/app/modules/shared/modals/update-app-modal/update-app-modal.component';
import { UiService } from '../ui.service';
import { LocalStorageService } from '../localStorage/localStorage.service';
import { localStorageKeys } from '../localStorage/localStorageVariables';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  constructor(
    private httpClient: HttpClient,
    private helperService: HelperService,
    private envService: TxService,
    private modalController: ModalController,
    private uiService: UiService,
    private localStorageService: LocalStorageService
  ) {}

  public set updateTypeApi(value: string) {}
  public get updateTypeApi(): string {
    const finalRequestParams = {
      ...this.envService.getDefaultApiParams(),
    };
    // tslint:disable-next-line: max-line-length
    return (this.updateTypeApi = `${this.envService.getUpdateTypeApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`);
  }

  public fetchUpdateType(): Observable<any> {
    return this.httpClient.get(this.updateTypeApi);
  }
  async handleAppUpdate(
    isOpenAppStore: boolean = false,
    isUpdateAvailableCheck: boolean = false
  ): Promise<boolean> {
    try {
      const updateAvailable = await this.isUpdateAvailable();
      if (updateAvailable) {
        const response = await firstValueFrom(this.fetchUpdateType());
        let forceUpdate = 0;
        forceUpdate =
          Capacitor.getPlatform() === 'android'
            ? response?.forceUpdateAndroid
            : response?.forceUpdateIOS;

        if (isOpenAppStore) {
          await this.openAppStore();
        } else {
          if (isUpdateAvailableCheck && forceUpdate === -1) {
            return false;
          } else if (isUpdateAvailableCheck && forceUpdate >= 0) {
            return true;
          }
          const isUpdateModal = this.localStorageService.getItem(
            localStorageKeys.IS_UPDATE_MODAL
          );
          if (forceUpdate === 0 && !isUpdateModal) {
            await this.openUpdateAppModal(forceUpdate);
          } else if (forceUpdate >= 1) {
            await this.openUpdateAppModal(forceUpdate);
          }
        }
      } else {
        if (
          this.localStorageService.getItem(localStorageKeys.IS_UPDATE_MODAL)
        ) {
          this.localStorageService.removeItem(localStorageKeys.IS_UPDATE_MODAL);
        }
        return false;
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
      return false;
    }
  }
  async openUpdateAppModal(forceUpdate: number) {
    const modal = await this.modalController.create({
      component: UpdateAppModalComponent,
      componentProps: {
        forceUpdate,
        initiateUpdate: async (forceUpdate) => {
          await this.openAppStore();
          // if (forceUpdate) {
          //   this.uiService.presentToast('forceUpdate');
          // } else {
          //   this.uiService.presentToast('noforceUpdate');
          //   const updateStatus = await this.startFlexibleUpdate();
          //   if (updateStatus?.code !== AppUpdateResultCode.OK) {
          //     await this.openAppStore();
          //   }
          // }
        },
      },
      mode: 'ios',
    });
    modal.present();
  }

  async getCurrentAppVersion(): Promise<string | number> {
    const result = await AppUpdate.getAppUpdateInfo();
    return Capacitor.getPlatform() === 'android'
      ? result.currentVersionCode
      : result.currentVersionName;
  }

  async getAvailableAppVersion(): Promise<string | number> {
    const result = await AppUpdate.getAppUpdateInfo();
    return Capacitor.getPlatform() === 'android'
      ? result.availableVersionCode
      : result.availableVersionName;
  }

  async isUpdateAvailable(): Promise<boolean> {
    try {
      const platform = Capacitor.getPlatform();
      const currentVersion = await this.getCurrentAppVersion();
      const availableVersion = await this.getAvailableAppVersion();

      if (!availableVersion || !currentVersion) {
        return false;
      }

      if (platform === 'android') {
        return availableVersion > currentVersion;
      }

      if (platform === 'ios') {
        return this.compareVersions(availableVersion, currentVersion) > 0;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  private compareVersions(v1: string | number, v2: string | number): number {
    const toParts = (v: string | number) =>
      (String(v).match(/^\d+(\.\d+)*/)?.[0] ?? '0').split('.').map(Number);

    const parts1 = toParts(v1);
    const parts2 = toParts(v2);
    const maxLength = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < maxLength; i++) {
      const num1 = parts1[i] ?? 0;
      const num2 = parts2[i] ?? 0;

      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }
    return 0;
  }

  async openAppStore(): Promise<void> {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      await AppUpdate.openAppStore();
    } else if (platform === 'ios') {
      const appId = 'id1663614675';
      await AppUpdate.openAppStore({ appId });
    }
  }

  async performImmediateUpdate(): Promise<AppUpdateResult | null> {
    const result = await AppUpdate.getAppUpdateInfo();
    if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
      return null;
    }
    if (result.immediateUpdateAllowed) {
      return await AppUpdate.performImmediateUpdate();
    }
  }

  async startFlexibleUpdate(): Promise<AppUpdateResult | null> {
    const result = await AppUpdate.getAppUpdateInfo();
    if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
      return null;
    }
    if (result.flexibleUpdateAllowed) {
      return await AppUpdate.startFlexibleUpdate();
    }
  }

  async completeFlexibleUpdate(): Promise<void> {
    await AppUpdate.completeFlexibleUpdate();
  }
}
